import { Link } from "wouter";
import { ArrowLeft, Shield } from "lucide-react";
import { PageMeta } from "@/components/PageMeta";

const LAST_UPDATED = "May 11, 2026";

interface SectionProps {
  number: string;
  title: string;
  children: React.ReactNode;
}

function Section({ number, title, children }: SectionProps) {
  return (
    <section className="mb-10">
      <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
        <span className="text-[#7C6EEF] font-mono text-sm font-bold bg-[#7C6EEF]/10 rounded-md px-2 py-0.5">
          {number}
        </span>
        {title}
      </h2>
      <div className="text-slate-300 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <h3 className="text-white font-semibold text-sm mb-2">{title}</h3>
      <div className="text-slate-300 space-y-2">{children}</div>
    </div>
  );
}

export function Privacy() {
  return (
    <div className="min-h-screen bg-[#030913] text-white">
      <PageMeta
        title="Privacy Policy — LaunchTactix"
        description="LaunchTactix Privacy Policy: how we collect, use, and protect your personal data. GDPR, CCPA, and global privacy compliance."
        canonical="https://launchtactix.com/privacy"
      />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm mb-10 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to LaunchTactix
        </Link>

        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-[#7C6EEF]/15 flex items-center justify-center">
            <Shield className="w-6 h-6 text-[#7C6EEF]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
            <p className="text-slate-400 text-sm mt-0.5">Last updated: {LAST_UPDATED}</p>
          </div>
        </div>

        <div className="h-px bg-gradient-to-r from-[#7C6EEF]/40 via-[#14B8A6]/20 to-transparent mb-10" />

        <Section number="1" title="Who We Are">
          <p>
            LaunchTactix ("we", "us", "our") operates the LaunchTactix web platform and mobile
            application — an AI-powered business growth suite providing lead generation, outreach
            automation, CRM, social media management, SEO, reputation monitoring, and sales
            analytics.
          </p>
          <p>
            For privacy enquiries:{" "}
            <a href="mailto:privacy@launchtactix.com" className="text-[#7C6EEF] hover:underline">
              privacy@launchtactix.com
            </a>
          </p>
        </Section>

        <Section number="2" title="Data We Collect">
          <p>We collect data in the following categories:</p>

          <SubSection title="Account & Identity Data">
            <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/[0.03]">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="py-3 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider w-48">Data Type</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {[
                    ["Name & email address", "Account creation, login, and communications"],
                    ["User ID", "Identifying your account across devices and sessions"],
                    ["Business profile", "Name, industry, products/services — used to personalise AI outputs"],
                    ["Device ID & session tokens", "Maintaining login sessions and delivering push notifications"],
                    ["Profile photo (optional)", "Personalising your account display"],
                  ].map(([type, purpose]) => (
                    <tr key={type} className="border-b border-white/5">
                      <td className="py-2.5 px-4 text-white font-medium text-sm">{type}</td>
                      <td className="py-2.5 px-4 text-slate-300 text-sm">{purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SubSection>

          <SubSection title="Business Operations Data">
            <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/[0.03]">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="py-3 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider w-48">Data Type</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {[
                    ["B2B lead & prospect data", "Company names, email addresses, websites, job titles — collected via enrichment providers on your behalf to power outreach campaigns"],
                    ["Outreach emails & sequences", "Email copy, send timestamps, reply/bounce/open events — to deliver and track your sales outreach"],
                    ["SMS conversations", "Messages sent and received via your connected Twilio number"],
                    ["CRM & deal data", "Pipeline stages, deal values, notes, and contact history"],
                    ["Social media posts & analytics", "Content you create and the engagement metrics returned by connected platforms"],
                    ["Social media access tokens", "OAuth tokens for Instagram, Facebook, LinkedIn, Twitter/X, and TikTok — stored encrypted, used only to publish posts on your behalf"],
                    ["Canva OAuth token", "Stored encrypted; used only to open designs in your Canva account"],
                    ["SMTP credentials", "Stored encrypted; used only to send outreach email on your behalf"],
                    ["SEO & reputation data", "Keyword rankings, review data, and competitor signals fetched from public sources on your behalf"],
                    ["AI-generated content", "Social posts, email copy, proposals, and images generated by the platform's AI features"],
                  ].map(([type, purpose]) => (
                    <tr key={type} className="border-b border-white/5">
                      <td className="py-2.5 px-4 text-white font-medium text-sm">{type}</td>
                      <td className="py-2.5 px-4 text-slate-300 text-sm">{purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SubSection>

          <SubSection title="Billing & Usage Data">
            <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/[0.03]">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="py-3 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider w-48">Data Type</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {[
                    ["Subscription & payment records", "Processed by Stripe (web) or Apple/Google (mobile) — we do not store card numbers"],
                    ["Feature usage & event logs", "Understanding how the platform is used to improve performance and surface AI recommendations"],
                    ["Audit logs", "Recording administrative actions for security and compliance purposes"],
                  ].map(([type, purpose]) => (
                    <tr key={type} className="border-b border-white/5">
                      <td className="py-2.5 px-4 text-white font-medium text-sm">{type}</td>
                      <td className="py-2.5 px-4 text-slate-300 text-sm">{purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SubSection>
        </Section>

        <Section number="3" title="How We Use Your Data">
          <p>We use your data to:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Create and manage your account and workspace</li>
            <li>Deliver all core platform features (leads, outreach, CRM, social, SEO, analytics)</li>
            <li>Generate AI-powered content and recommendations using your business context</li>
            <li>Send outreach emails and SMS on your behalf to your specified recipients</li>
            <li>Publish social media posts to your connected platform accounts</li>
            <li>Enrich and verify lead data via third-party data providers</li>
            <li>Process subscription payments and manage your billing</li>
            <li>Send transactional notifications (billing receipts, alerts, sequence activity)</li>
            <li>Improve platform performance, reliability, and AI model outputs</li>
            <li>Respond to support requests</li>
            <li>Comply with legal and regulatory obligations</li>
          </ul>
          <p className="mt-3 p-3 rounded-lg bg-[#14B8A6]/10 border border-[#14B8A6]/20 text-[#14B8A6] text-sm font-medium">
            We do not use your data for targeted advertising, and we do not sell your data to third parties.
          </p>
        </Section>

        <Section number="4" title="AI Processing">
          <p>
            LaunchTactix uses AI models to generate content, score leads, classify emails, and
            produce images on your behalf. When you use AI features, relevant context from your
            account (such as your business profile, lead details, or post topic) is sent to the
            AI model to generate a response.
          </p>
          <p>
            AI features are powered via the{" "}
            <span className="text-white font-medium">Replit AI Integrations proxy</span>, which
            routes requests to OpenAI's models (GPT-4o for text, DALL-E 3 for images). Your
            prompts and outputs are subject to{" "}
            <a
              href="https://openai.com/policies/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#7C6EEF] hover:underline"
            >
              OpenAI's privacy policy
            </a>
            .
          </p>
          <p>
            We do not use your data to train AI models, and AI-generated outputs are stored only
            as part of your account data (for example, a saved social post).
          </p>
        </Section>

        <Section number="5" title="Data Sharing & Third-Party Services">
          <p>
            We share data only as necessary to deliver the platform. Below is a complete list of
            the third-party services we use and what data they receive:
          </p>

          <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/[0.03] mt-4">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider w-40">Service</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider w-40">Purpose</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Data shared</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {[
                  ["Clerk", "Authentication", "Name, email address, session tokens"],
                  ["Stripe", "Web subscription billing", "Email, subscription tier — card details handled directly by Stripe"],
                  ["RevenueCat", "Mobile in-app purchases", "User ID, purchase history"],
                  ["Apple App Store / Google Play", "Mobile payment processing", "Managed entirely by Apple/Google"],
                  ["SendGrid", "Transactional & outreach email delivery", "Recipient email addresses, email content, sender name/address"],
                  ["Twilio", "SMS outreach", "Recipient phone numbers, message content"],
                  ["OpenAI (via Replit)", "AI content & image generation", "Business profile context, post topics, lead data snippets used as prompts"],
                  ["Hunter.io", "Lead email discovery & verification", "Email addresses and domains submitted for lookup or verification"],
                  ["Apollo.io", "Lead contact & company enrichment", "Company names and domains submitted for enrichment"],
                  ["Crunchbase", "Funding & investor data", "Company names and domains"],
                  ["BuiltWith / Wappalyzer", "Technology stack detection", "Website domains"],
                  ["OpenCorporates", "Legal entity verification", "Company names"],
                  ["Canva", "Branded design editing", "OAuth token; post image data when you open a design"],
                  ["Instagram / Facebook", "Social publishing", "Post content, images, captions — via your connected OAuth token"],
                  ["LinkedIn", "Social publishing", "Post content — via your connected OAuth token"],
                  ["Twitter / X", "Social publishing", "Post content — via your connected OAuth token"],
                  ["TikTok", "Social publishing", "Post content — via your connected OAuth token"],
                  ["Replit", "Hosting, database & storage infrastructure", "All platform data is hosted on Replit's infrastructure"],
                ].map(([service, purpose, data]) => (
                  <tr key={service} className="border-b border-white/5">
                    <td className="py-2.5 px-4 text-white font-medium text-sm">{service}</td>
                    <td className="py-2.5 px-4 text-slate-400 text-sm">{purpose}</td>
                    <td className="py-2.5 px-4 text-slate-300 text-sm">{data}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-4">
            All third-party partners are bound by data processing agreements and are prohibited
            from using your data for their own purposes beyond what is listed above.
          </p>
          <p>
            We do not share your data with law enforcement or regulators except where required
            by applicable law, and we will notify you where legally permitted to do so.
          </p>
        </Section>

        <Section number="6" title="B2B Lead & Prospect Data">
          <p>
            LaunchTactix is a B2B sales platform. As part of its core function, it discovers and
            enriches data about business professionals — including names, business email addresses,
            job titles, company names, and websites — sourced from public directories, professional
            networks, and licensed data providers.
          </p>
          <p>
            This data relates to individuals acting in a professional capacity and is used solely
            for the purpose of facilitating legitimate B2B sales outreach on your behalf. We do
            not collect or process sensitive personal data (such as health, financial, or political
            data) about prospects.
          </p>
          <p>
            If you are a business professional whose data appears in our platform and you wish to
            have it removed, contact us at{" "}
            <a href="mailto:privacy@launchtactix.com" className="text-[#7C6EEF] hover:underline">
              privacy@launchtactix.com
            </a>
            .
          </p>
        </Section>

        <Section number="7" title="Data Storage & Security">
          <p>
            All data is stored on encrypted servers hosted by Replit and transmitted exclusively
            over HTTPS/TLS. Sensitive credentials (SMTP passwords, social media tokens, API keys)
            are encrypted at rest using AES-256-GCM before storage and are never logged or
            exposed in the UI.
          </p>
          <p>
            We retain your account data for as long as your account is active. Upon account
            deletion, we remove your data within 30 days, except where retention is required
            by law (for example, financial records).
          </p>
          <p>
            We maintain audit logs of administrative and security-sensitive actions for a
            minimum of 90 days.
          </p>
        </Section>

        <Section number="8" title="Your Rights">
          <p>
            Depending on your location (including under GDPR, the Australian Privacy Act, and
            CCPA), you may have the right to:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li><span className="text-white font-medium">Access</span> the personal data we hold about you</li>
            <li><span className="text-white font-medium">Correct</span> inaccurate or incomplete data</li>
            <li><span className="text-white font-medium">Delete</span> your account and all associated data</li>
            <li><span className="text-white font-medium">Export</span> your data in a portable format</li>
            <li><span className="text-white font-medium">Restrict or object</span> to certain processing activities</li>
            <li><span className="text-white font-medium">Withdraw consent</span> at any time (where processing is based on consent)</li>
            <li><span className="text-white font-medium">Lodge a complaint</span> with your local data protection authority</li>
          </ul>
          <p>
            To exercise any of these rights, contact us at{" "}
            <a href="mailto:privacy@launchtactix.com" className="text-[#7C6EEF] hover:underline">
              privacy@launchtactix.com
            </a>{" "}
            or use <span className="text-white font-medium">Settings → Delete Account</span> within
            the app. We will respond within 30 days.
          </p>
        </Section>

        <Section number="9" title="Subscriptions & Billing">
          <p>
            Web subscriptions are managed through{" "}
            <span className="text-white font-medium">Stripe</span>. Mobile subscriptions are
            managed through the{" "}
            <span className="text-white font-medium">Apple App Store</span> (iOS) and{" "}
            <span className="text-white font-medium">Google Play</span> (Android). Payment card
            details are handled entirely by these platforms — LaunchTactix never stores card
            numbers, CVV codes, or bank account details.
          </p>
          <p>
            To cancel a web subscription, go to{" "}
            <span className="text-white font-medium">Settings → Billing</span> in the platform. To
            cancel a mobile subscription, manage it through your device's app store account settings.
          </p>
        </Section>

        <Section number="10" title="Push Notifications & SMS">
          <p>
            We may send push notifications for lead alerts, deal updates, and outreach sequence
            activity. You can disable these at any time in your device settings or within the app.
          </p>
          <p>
            SMS features are opt-in and use your own Twilio number. You are responsible for
            ensuring compliance with applicable telemarketing laws (including obtaining consent
            from recipients) when sending SMS via the platform.
          </p>
        </Section>

        <Section number="11" title="Cookies & Tracking">
          <p>
            The LaunchTactix web platform uses session cookies and local storage solely to
            maintain your authenticated session. We do not use third-party advertising cookies,
            tracking pixels, or analytics cookies that follow you across other websites.
          </p>
        </Section>

        <Section number="12" title="Children's Privacy">
          <p>
            LaunchTactix is a B2B platform intended for business use only and is not directed at
            individuals under the age of 18. We do not knowingly collect data from anyone under
            18. If we become aware that we have, we will delete it immediately.
          </p>
        </Section>

        <Section number="13" title="Changes to This Policy">
          <p>
            We may update this policy from time to time as the platform evolves. We will notify
            you of material changes via email or an in-app notice at least 14 days before they
            take effect. The "Last updated" date at the top of this page always reflects the most
            current version. Continued use of the platform after changes take effect constitutes
            acceptance of the revised policy.
          </p>
        </Section>

        <Section number="14" title="Contact">
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-1 text-sm">
            <p className="text-white font-semibold text-base mb-2">LaunchTactix</p>
            <p>
              Privacy enquiries:{" "}
              <a href="mailto:privacy@launchtactix.com" className="text-[#7C6EEF] hover:underline">
                privacy@launchtactix.com
              </a>
            </p>
            <p>
              General support:{" "}
              <a href="mailto:support@launchtactix.com" className="text-[#7C6EEF] hover:underline">
                support@launchtactix.com
              </a>
            </p>
            <p className="mt-2">
              <a href="https://launchtactix.com/privacy" className="text-[#7C6EEF] hover:underline">
                https://launchtactix.com/privacy
              </a>
            </p>
          </div>
        </Section>

        <div className="h-px bg-gradient-to-r from-[#7C6EEF]/40 via-[#14B8A6]/20 to-transparent mt-12 mb-8" />

        <p className="text-center text-slate-500 text-sm">
          © 2026 LaunchTactix · All rights reserved ·{" "}
          <a href="mailto:privacy@launchtactix.com" className="hover:text-slate-300 transition-colors">
            privacy@launchtactix.com
          </a>
        </p>
      </div>
    </div>
  );
}
